/**
 * repurpose/Output.jsx
 * One channel's copy, as written.
 *
 * A thread is shown as its parts and a carousel as its slides, because that is
 * what will be pasted — one block of prose with "1/5" buried in it is not what
 * anybody needs at the other end.
 *
 * The copied text comes from the server, so what lands on the clipboard is
 * exactly what the server rendered, not a second version assembled here.
 *
 * Dependencies: components/Icons.jsx
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Clipboard, Edit, Sparkle, Warning } from '../Icons.jsx';

export default function Output({ channel, output, text, onDraft, drafting, savedPostId }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text ?? '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard access can be refused; the text is on screen and selectable,
      // so this is a missing convenience rather than a failure worth an alert.
      setCopied(false);
    }
  }

  return (
    <article className="rp-output card">
      <header className="rp-output__head">
        <h3>{channel.label}</h3>
        <div className="rp-output__actions">
          <button type="button" className="btn btn--ghost btn--sm" onClick={copy}>
            {copied ? <Check size={14} /> : <Clipboard size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </button>

          {channel.draftable &&
            (savedPostId ? (
              <Link className="btn btn--sm" to="/drafts">
                <Check size={14} /> In Drafts
              </Link>
            ) : (
              <button
                type="button"
                className="btn btn--sm"
                onClick={() => onDraft(channel.key)}
                disabled={drafting}
              >
                <Edit size={14} />
                {drafting ? 'Saving…' : 'Save as draft'}
              </button>
            ))}
        </div>
      </header>

      <Body channel={channel} output={output} />

      {output.hashtags && <p className="rp-tags">{output.hashtags}</p>}

      {!channel.draftable && (
        <p className="rp-output__note">
          <Warning size={13} /> {channel.reason}
        </p>
      )}
    </article>
  );
}

function Body({ channel, output }) {
  if (channel.parts && Array.isArray(output.parts)) {
    return (
      <ol className="rp-parts">
        {output.parts.map((part, i) => (
          <li key={i}>
            <span className="rp-parts__n">
              {i + 1}/{output.parts.length}
            </span>
            <p>{part}</p>
            <span className="rp-parts__len">{part.length}</span>
          </li>
        ))}
      </ol>
    );
  }

  if (channel.slides && Array.isArray(output.slides)) {
    return (
      <>
        <ol className="rp-slides">
          {output.slides.map((slide, i) => (
            <li key={i}>
              <span className="rp-slides__n">{i + 1}</span>
              <strong>{slide.heading}</strong>
              <p>{slide.body}</p>
            </li>
          ))}
        </ol>
        {output.caption && (
          <p className="rp-output__caption">
            <span className="muted">Caption</span>
            {output.caption}
          </p>
        )}
      </>
    );
  }

  return (
    <>
      {output.title && <p className="rp-output__title">{output.title}</p>}
      <p className="rp-output__text">{output.text}</p>
      <span className="rp-output__len muted">
        {(output.text ?? '').length.toLocaleString()} / {channel.maxChars.toLocaleString()}
      </span>
    </>
  );
}

/** The line that keeps AI output from reading as measurement. */
export function WrittenBy({ model, at }) {
  if (!model) return null;
  return (
    <p className="rp-writtenby">
      <Sparkle size={13} /> Written by {model}
      {at ? ` on ${new Date(at).toLocaleString('en-GB')}` : ''}. Read it before you post it.
    </p>
  );
}
